// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- Direct source consumers include CSS types.
/// <reference path="./styles.d.ts" />
/* eslint-disable react-refresh/only-export-components */
import {
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createRoot, type Root } from "react-dom/client";
import {
  Button,
  Dialog,
  InlineFeedback,
  Spinner,
  type ButtonProps,
} from "./controls";
import "./Feedback.css";

type Tone = "info" | "success" | "warning" | "error" | "loading";
export interface MessageOptions {
  content: ReactNode;
  key?: string | number;
  duration?: number;
  onClose?: () => void;
  icon?: ReactNode;
  className?: string;
  style?: CSSProperties;
}
export interface MessageHandle extends PromiseLike<void> {
  (): void;
}
export interface ConfirmationOptions {
  title?: ReactNode;
  content?: ReactNode;
  okText?: ReactNode;
  cancelText?: ReactNode;
  okType?: ButtonProps["type"];
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  onOk?: () => unknown;
  onCancel?: () => unknown;
  afterClose?: () => void;
  width?: number | string;
  centered?: boolean;
  closable?: boolean;
  maskClosable?: boolean;
  className?: string;
  // Empty names are accepted during migration; Ark's motion policy owns effects.
  transitionName?: string;
  maskTransitionName?: string;
}
export interface ConfirmationHandle extends PromiseLike<boolean> {
  destroy: () => void;
  update: (
    next:
      | Partial<ConfirmationOptions>
      | ((current: ConfirmationOptions) => Partial<ConfirmationOptions>),
  ) => void;
}
interface ToastRecord extends MessageOptions {
  id: number;
  tone: Tone;
  settle: () => void;
  timer?: ReturnType<typeof setTimeout>;
}
interface ConfirmationRecord {
  id: number;
  options: ConfirmationOptions;
  settle: (result: boolean) => void;
}
interface Snapshot {
  toasts: ToastRecord[];
  confirmations: ConfirmationRecord[];
}
interface FeedbackRuntime {
  serial: number;
  snapshot: Snapshot;
  listeners: Set<() => void>;
  root?: Root;
}
const runtimeKey = Symbol.for("ark.feedback.runtime.v1");
type FeedbackWindow = Window & { [runtimeKey]?: FeedbackRuntime };
const serverRuntime: FeedbackRuntime = {
  serial: 0,
  snapshot: { toasts: [], confirmations: [] },
  listeners: new Set(),
};
function runtime() {
  if (typeof window === "undefined") return serverRuntime;
  return ((window as FeedbackWindow)[runtimeKey] ??= {
    serial: 0,
    snapshot: { toasts: [], confirmations: [] },
    listeners: new Set(),
  });
}
function emit(state: FeedbackRuntime, next: Snapshot) {
  state.snapshot = next;
  state.listeners.forEach((listener) => listener());
}
function ensureHost(state: FeedbackRuntime) {
  if (state.root || typeof document === "undefined") return;
  const element = document.createElement("div");
  element.dataset.arkFeedbackHost = "";
  document.body.append(element);
  state.root = createRoot(element);
  state.root.render(<FeedbackHost state={state} />);
}
function removeToast(state: FeedbackRuntime, id: number) {
  const record = state.snapshot.toasts.find((entry) => entry.id === id);
  if (!record) return;
  clearTimeout(record.timer);
  emit(state, {
    ...state.snapshot,
    toasts: state.snapshot.toasts.filter((entry) => entry.id !== id),
  });
  record.settle();
  record.onClose?.();
}
function showMessage(
  tone: Tone,
  value: ReactNode | MessageOptions,
  duration?: number,
  onClose?: () => void,
): MessageHandle {
  const state = runtime();
  ensureHost(state);
  const options: MessageOptions =
    value && typeof value === "object" && "content" in value
      ? value
      : {
          content: value,
          ...(duration === undefined ? {} : { duration }),
          ...(onClose === undefined ? {} : { onClose }),
        };
  const previous =
    options.key === undefined
      ? undefined
      : state.snapshot.toasts.find((entry) => entry.key === options.key);
  if (previous) removeToast(state, previous.id);
  const id = ++state.serial;
  let settle = () => {};
  const promise = new Promise<void>((resolve) => {
    settle = resolve;
  });
  const record: ToastRecord = { ...options, id, tone, settle };
  const seconds = options.duration ?? (tone === "loading" ? 0 : 3);
  if (seconds > 0)
    record.timer = setTimeout(() => removeToast(state, id), seconds * 1000);
  emit(state, {
    ...state.snapshot,
    toasts: [...state.snapshot.toasts, record],
  });
  return Object.assign(() => removeToast(state, id), {
    then: promise.then.bind(promise),
  });
}
function closeConfirmation(state: FeedbackRuntime, id: number, result = false) {
  const record = state.snapshot.confirmations.find((entry) => entry.id === id);
  if (!record) return;
  emit(state, {
    ...state.snapshot,
    confirmations: state.snapshot.confirmations.filter(
      (entry) => entry.id !== id,
    ),
  });
  record.settle(result);
  record.options.afterClose?.();
}
function confirm(options: ConfirmationOptions): ConfirmationHandle {
  const state = runtime();
  ensureHost(state);
  const id = ++state.serial;
  let settle: (result: boolean) => void = () => {};
  const promise = new Promise<boolean>((resolve) => {
    settle = resolve;
  });
  emit(state, {
    ...state.snapshot,
    confirmations: [...state.snapshot.confirmations, { id, options, settle }],
  });
  return {
    destroy: () => closeConfirmation(state, id),
    update: (next) =>
      emit(state, {
        ...state.snapshot,
        confirmations: state.snapshot.confirmations.map((entry) =>
          entry.id === id
            ? {
                ...entry,
                options: {
                  ...entry.options,
                  ...(typeof next === "function" ? next(entry.options) : next),
                },
              }
            : entry,
        ),
      }),
    then: promise.then.bind(promise),
  };
}
function Confirmation({
  state,
  record,
}: {
  state: FeedbackRuntime;
  record: ConfirmationRecord;
}) {
  const submitting = useRef(false);
  const [pending, setPending] = useState<"ok" | "cancel">();
  const [error, setError] = useState("");
  const { options } = record;
  async function submit(action: "ok" | "cancel") {
    if (submitting.current) return;
    submitting.current = true;
    setPending(action);
    setError("");
    try {
      const result = await (action === "ok"
        ? options.onOk?.()
        : options.onCancel?.());
      if (result !== false)
        closeConfirmation(state, record.id, action === "ok");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "操作未完成，请重试");
    } finally {
      submitting.current = false;
      setPending(undefined);
    }
  }
  return (
    <Dialog
      open
      title={options.title ?? "确认操作"}
      width={options.width}
      className={options.className}
      closeOnBackdrop={options.maskClosable ?? false}
      onClose={() => {
        if (options.closable !== false) void submit("cancel");
      }}
      footer={
        <>
          <Button
            {...options.cancelButtonProps}
            disabled={Boolean(pending) || options.cancelButtonProps?.disabled}
            loading={pending === "cancel"}
            onClick={() => void submit("cancel")}
          >
            {options.cancelText ?? "取消"}
          </Button>
          <Button
            type={options.okType ?? "primary"}
            {...options.okButtonProps}
            disabled={Boolean(pending) || options.okButtonProps?.disabled}
            loading={pending === "ok"}
            onClick={() => void submit("ok")}
          >
            {options.okText ?? "确认"}
          </Button>
        </>
      }
    >
      {options.content}
      {error ? (
        <InlineFeedback className="ark-feedback-error" type="error">
          {error}
        </InlineFeedback>
      ) : null}
    </Dialog>
  );
}
function FeedbackHost({ state }: { state: FeedbackRuntime }) {
  const snapshot = useSyncExternalStore(
    (listener) => {
      state.listeners.add(listener);
      return () => {
        state.listeners.delete(listener);
      };
    },
    () => state.snapshot,
    () => serverRuntime.snapshot,
  );
  return (
    <>
      <div className="ark-feedback-toasts" aria-label="操作提示">
        {snapshot.toasts.map((entry) => (
          <div
            key={entry.id}
            className={["ark-feedback-toast", entry.className]
              .filter(Boolean)
              .join(" ")}
            data-tone={entry.tone}
            style={entry.style}
            role={
              entry.tone === "error" || entry.tone === "warning"
                ? "alert"
                : "status"
            }
          >
            {entry.icon ??
              (entry.tone === "loading" ? <Spinner size="small" /> : null)}
            <div className="ark-feedback-content">{entry.content}</div>
            <Button
              className="ark-feedback-dismiss"
              type="unstyled"
              aria-label="关闭提示"
              onClick={() => removeToast(state, entry.id)}
            >
              ×
            </Button>
          </div>
        ))}
      </div>
      {snapshot.confirmations.map((record) => (
        <Confirmation key={record.id} state={state} record={record} />
      ))}
    </>
  );
}

export const message = {
  info: (
    value: ReactNode | MessageOptions,
    duration?: number,
    onClose?: () => void,
  ) => showMessage("info", value, duration, onClose),
  success: (
    value: ReactNode | MessageOptions,
    duration?: number,
    onClose?: () => void,
  ) => showMessage("success", value, duration, onClose),
  warning: (
    value: ReactNode | MessageOptions,
    duration?: number,
    onClose?: () => void,
  ) => showMessage("warning", value, duration, onClose),
  error: (
    value: ReactNode | MessageOptions,
    duration?: number,
    onClose?: () => void,
  ) => showMessage("error", value, duration, onClose),
  loading: (
    value: ReactNode | MessageOptions,
    duration?: number,
    onClose?: () => void,
  ) => showMessage("loading", value, duration, onClose),
  open: (options: MessageOptions & { type?: Tone }) =>
    showMessage(options.type ?? "info", options),
  destroy: (key?: string | number) => {
    const state = runtime();
    state.snapshot.toasts
      .filter((entry) => key === undefined || entry.key === key)
      .forEach((entry) => removeToast(state, entry.id));
  },
};
export interface NotificationOptions extends Omit<MessageOptions, "content"> {
  message: ReactNode;
  description?: ReactNode;
}
function notify(
  tone: Tone,
  { message: title, description, ...options }: NotificationOptions,
) {
  return showMessage(tone, {
    ...options,
    content: (
      <>
        <strong>{title}</strong>
        {description ? <div>{description}</div> : null}
      </>
    ),
  });
}
export const modal = { confirm };
export const notification = {
  info: (options: NotificationOptions) => notify("info", options),
  success: (options: NotificationOptions) => notify("success", options),
  warning: (options: NotificationOptions) => notify("warning", options),
  error: (options: NotificationOptions) => notify("error", options),
};
const feedback = { message, modal, notification };
export function useFeedback() {
  return feedback;
}
